import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MenuComponent } from '@components/header/menu';
import { MenuBlogComponent } from '@components/header/menu-blog';
import { IsHeaderDefaultPipe, IsHeaderTunnelPipe } from '@components/header/pipes';
import { PromotionComponent } from '@components/promotion';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageComponent } from '@shared/components/image';
import { SvgComponent } from '@shared/components/svg';
import { LogsService } from '@shared/services/logs';
import { ResponsiveService } from '@shared/services/responsive';
import { BudgetEstimatorStore, CommonStore, FunnelStore, PageStore } from '@stores';

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    SvgComponent,
    MenuComponent,
    IsHeaderDefaultPipe,
    IsHeaderTunnelPipe,
    NgClass,
    TranslatePipe,
    PromotionComponent,
    MenuBlogComponent,
    ImageComponent,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  protected isMenuOpen = signal(false);
  protected headerStore = inject(PageStore).header;
  protected headerSubMenuStore = inject(PageStore).headerSubMenu;
  protected readonly breakpoints = inject(ResponsiveService).breakpoints;
  private readonly appointmentFunnel = inject(CommonStore).select('appointmentFunnel');
  // The main headers leave their logo link empty: it then leads to the home of the page's language.
  protected readonly baseUrl = inject(CommonStore).select('baseUrl');
  private readonly funnelStore = inject(FunnelStore);
  protected funnelCurrentStep = this.funnelStore.select('currentStep');
  // Read in the step links: an @let read there breaks this header's hydration under ng serve HMR (NG0502).
  protected readonly funnelStep = computed(() => this.funnelCurrentStep() ?? 1);
  private readonly budgetEstimatorStore = inject(BudgetEstimatorStore);
  readonly isBudgetEstimatorPage = this.budgetEstimatorStore.select('isBudgetEstimatorPage');
  readonly budgetEstimatorCurrentStep = this.budgetEstimatorStore.select('currentStep');
  private readonly router = inject(Router);
  private readonly logs = inject(LogsService);

  toggleMenu(): void {
    this.isMenuOpen.update((isOpen) => !isOpen);
  }

  onStepClick(stepNumber: number): void {
    const appointmentStepsUrls = this.appointmentFunnel()?.urls;
    switch (stepNumber) {
      case 1:
        void this.router
          .navigate([appointmentStepsUrls?.step1])
          .then(() => {
            this.funnelStore.batchUpdate({
              update: {
                validatedStep: 0,
              },
              delete: ['storeSelected', 'slotSelected'],
            });
          })
          .catch((err: unknown) => this.logs.error('HeaderComponent', 'Navigation to appointment step 1 failed', err));
        break;
      case 2:
        void this.router
          .navigate([appointmentStepsUrls?.step2])
          .then(() => {
            this.funnelStore.batchUpdate({
              update: {
                validatedStep: 1,
              },
              delete: ['slotSelected'],
            });
          })
          .catch((err: unknown) => this.logs.error('HeaderComponent', 'Navigation to appointment step 2 failed', err));
        break;
    }
  }
}
